import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
// @ts-ignore
import WifiOff from 'lucide-react/dist/esm/icons/wifi-off';
import {
  buscarEmpresaPorCnpj,
  cadastrarConferente,
  loginConferenteCpf,
  alterarPinConferente,
  isConferenteLoggedIn,
  setConferenteCnpjSlug,
  EmpresaInfoPublica
} from '../../services/conferente';
import { sincronizarConfiguracoes, sincronizarMontagensCargas, sincronizarConferencias } from '../../services/offlineData';
import { getUser as getMainUser, isAuthenticated as isMainAuthenticated } from '../../services/config';
import PWAInstallPrompt from '../../components/PWA/PWAInstallPrompt';
import { useOnlineStatus } from '../../hooks/useOnlineStatus';
import { useDynamicManifest } from '../../hooks/useDynamicManifest';
import '../../components/Auth/Login.css';

// =============================================
// CONTAS SALVAS DIARISTA (localStorage)
// =============================================
const SAVED_DIARISTAS_KEY = 'conferente_saved_diaristas';

interface SavedDiarista {
  cpf: string; // somente digitos
  nome: string;
  cnpj: string; // CNPJ da transportadora
}

function getSavedDiaristas(cnpj: string): SavedDiarista[] {
  try {
    const raw = localStorage.getItem(SAVED_DIARISTAS_KEY);
    if (!raw) return [];
    const all: SavedDiarista[] = JSON.parse(raw);
    return all.filter(d => d.cnpj === cnpj);
  } catch {
    return [];
  }
}

function saveDiarista(cpf: string, nome: string, cnpj: string) {
  let all: SavedDiarista[] = [];
  try {
    const raw = localStorage.getItem(SAVED_DIARISTAS_KEY);
    if (raw) all = JSON.parse(raw);
  } catch { /* vazio */ }
  // Remove duplicado (mesmo CPF + CNPJ)
  all = all.filter(d => !(d.cpf === cpf && d.cnpj === cnpj));
  all.unshift({ cpf, nome, cnpj });
  localStorage.setItem(SAVED_DIARISTAS_KEY, JSON.stringify(all));
}

function removeDiarista(cpf: string, cnpj: string) {
  let all: SavedDiarista[] = [];
  try {
    const raw = localStorage.getItem(SAVED_DIARISTAS_KEY);
    if (raw) all = JSON.parse(raw);
  } catch { /* vazio */ }
  all = all.filter(d => !(d.cpf === cpf && d.cnpj === cnpj));
  localStorage.setItem(SAVED_DIARISTAS_KEY, JSON.stringify(all));
}

function maskCpf(cpf: string): string {
  if (cpf.length !== 11) return '***.***.***-**';
  return `${cpf.slice(0, 3)}.***.***-**`;
}

function getInitials(nome: string): string {
  return nome.split(' ').map(p => p[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
}

// =============================================
// COMPONENTE
// =============================================
type Modo = 'loading' | 'login' | 'cadastro' | 'alterar_pin' | 'erro';

const ConferenteCadastroLogin: React.FC = () => {
  const navigate = useNavigate();
  const { cnpj } = useParams<{ cnpj: string }>();
  const isOnline = useOnlineStatus();

  // Manifest dinâmico para que o PWA seja instalado com start_url = /conferente/:cnpj
  useDynamicManifest(cnpj ? `/conferente/${cnpj}` : '');

  const [modo, setModo] = useState<Modo>('loading');
  const [empresa, setEmpresa] = useState<EmpresaInfoPublica | null>(null);
  const [erroEmpresa, setErroEmpresa] = useState('');

  // Campos do formulário
  const [cpf, setCpf] = useState('');
  const [nome, setNome] = useState('');
  const [pin, setPin] = useState('');
  const [pinConfirm, setPinConfirm] = useState('');
  const [pinAtual, setPinAtual] = useState('');
  const [pinNovo, setPinNovo] = useState('');
  const [pinNovoConfirm, setPinNovoConfirm] = useState('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [syncStatus, setSyncStatus] = useState('');
  const [conferenteId, setConferenteId] = useState<number | null>(null);

  // Contas salvas
  const [savedDiaristas, setSavedDiaristas] = useState<SavedDiarista[]>([]);
  const [showSavedAccounts, setShowSavedAccounts] = useState(true);
  const [selectedAccount, setSelectedAccount] = useState<SavedDiarista | null>(null);

  // Carregar dados da empresa pelo CNPJ
  useEffect(() => {
    if (isConferenteLoggedIn()) {
      navigate('/conferente', { replace: true });
      return;
    }

    if (!cnpj) {
      setErroEmpresa('CNPJ não informado na URL');
      setModo('erro');
      return;
    }

    buscarEmpresaPorCnpj(cnpj)
      .then(data => {
        setEmpresa(data.empresa);
        setConferenteCnpjSlug(cnpj);
        setSavedDiaristas(getSavedDiaristas(cnpj));
        setModo('login');
      })
      .catch(err => {
        setErroEmpresa(err.message || 'Empresa não encontrada');
        setModo('erro');
      });
  }, [cnpj, navigate]);

  const formatCpf = (value: string) => {
    const nums = value.replace(/\D/g, '').slice(0, 11);
    if (nums.length <= 3) return nums;
    if (nums.length <= 6) return `${nums.slice(0, 3)}.${nums.slice(3)}`;
    if (nums.length <= 9) return `${nums.slice(0, 3)}.${nums.slice(3, 6)}.${nums.slice(6)}`;
    return `${nums.slice(0, 3)}.${nums.slice(3, 6)}.${nums.slice(6, 9)}-${nums.slice(9)}`;
  };

  const doSync = useCallback(async () => {
    setSyncStatus('Preparando dados offline...');
    try {
      await sincronizarConfiguracoes();
      setSyncStatus('Sincronizando montagens...');
      await sincronizarMontagensCargas();
      setSyncStatus('Sincronizando conferencias...');
      await sincronizarConferencias();
    } catch {
      // Continuar mesmo com erro
    }
  }, []);

  const doLoginRequest = useCallback(async (cpfLimpo: string, pinValue: string) => {
    if (!cnpj) return;

    const dispositivo_info = {
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      language: navigator.language,
      screenWidth: window.screen.width,
      screenHeight: window.screen.height,
      standalone: (window.navigator as any).standalone || window.matchMedia('(display-mode: standalone)').matches
    };

    const data = await loginConferenteCpf(cnpj, cpfLimpo, pinValue, dispositivo_info);

    // Salvar conta após login bem-sucedido
    saveDiarista(cpfLimpo, data.conferente.nome, cnpj);
    setSavedDiaristas(getSavedDiaristas(cnpj));

    if (data.pin_temporario) {
      setConferenteId(data.conferente.id);
      setPinAtual(pinValue);
      setModo('alterar_pin');
      setLoading(false);
      return;
    }

    await doSync();
    navigate('/conferente', { replace: true });
  }, [cnpj, doSync, navigate]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!cnpj || loading) return;

    const cpfLimpo = cpf.replace(/\D/g, '');
    if (cpfLimpo.length !== 11) {
      setError('CPF deve ter 11 dígitos');
      return;
    }
    if (pin.length < 4) {
      setError('PIN deve ter pelo menos 4 dígitos');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await doLoginRequest(cpfLimpo, pin);
    } catch (err: any) {
      if (err.message?.includes('não encontrado') || err.message?.includes('nao_cadastrado')) {
        setModo('cadastro');
        setError('');
      } else {
        setError(err.message || 'Erro ao fazer login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSavedAccountLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedAccount || !cnpj || loading) return;

    if (pin.length < 4) {
      setError('PIN deve ter pelo menos 4 dígitos');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await doLoginRequest(selectedAccount.cpf, pin);
    } catch (err: any) {
      setError(err.message || 'Erro ao fazer login');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAccount = (account: SavedDiarista) => {
    setSelectedAccount(account);
    setPin('');
    setError('');
  };

  const handleRemoveAccount = (e: React.MouseEvent, accountCpf: string) => {
    e.stopPropagation();
    if (!cnpj) return;
    removeDiarista(accountCpf, cnpj);
    setSavedDiaristas(getSavedDiaristas(cnpj));
    if (selectedAccount?.cpf === accountCpf) {
      setSelectedAccount(null);
      setPin('');
    }
  };

  const handleBackToAccounts = () => {
    setSelectedAccount(null);
    setPin('');
    setError('');
  };

  const handleCadastro = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!cnpj || loading) return;

    const cpfLimpo = cpf.replace(/\D/g, '');
    if (cpfLimpo.length !== 11) {
      setError('CPF deve ter 11 dígitos');
      return;
    }
    if (!nome.trim()) {
      setError('Nome é obrigatório');
      return;
    }
    if (pin.length < 4) {
      setError('PIN deve ter pelo menos 4 dígitos');
      return;
    }
    if (pin !== pinConfirm) {
      setError('Os PINs não conferem');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const dispositivo_info = {
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        language: navigator.language,
        screenWidth: window.screen.width,
        screenHeight: window.screen.height,
        standalone: (window.navigator as any).standalone || window.matchMedia('(display-mode: standalone)').matches
      };

      const data = await cadastrarConferente(cnpj, { cpf: cpfLimpo, nome: nome.trim(), pin, dispositivo_info });

      // Salvar conta após cadastro bem-sucedido
      saveDiarista(cpfLimpo, data.conferente?.nome || nome.trim(), cnpj);
      setSavedDiaristas(getSavedDiaristas(cnpj));

      await doSync();
      navigate('/conferente', { replace: true });
    } catch (err: any) {
      if (err.message?.includes('já possui cadastro')) {
        setModo('login');
        setError('Você já possui cadastro nesta transportadora. Faça login.');
      } else {
        setError(err.message || 'Erro no cadastro');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAlterarPin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!conferenteId || loading) return;

    if (pinNovo.length < 4) {
      setError('Novo PIN deve ter pelo menos 4 dígitos');
      return;
    }
    if (pinNovo !== pinNovoConfirm) {
      setError('Os PINs não conferem');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await alterarPinConferente(pinAtual, pinNovo);
      await doSync();
      navigate('/conferente', { replace: true });
    } catch (err: any) {
      setError(err.message || 'Erro ao alterar PIN');
    } finally {
      setLoading(false);
    }
  };

  // Tela de erro (empresa não encontrada)
  if (modo === 'erro') {
    return (
      <div className="login-page" style={{ background: 'linear-gradient(135deg, #16a34a 0%, #15803d 50%, #14532d 100%)' }}>
        <div className="login-container">
          <div className="logo-section">
            <img className="logo" src="/images/logo-confereai.png" alt="Logo ConfereAi"
              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
            <p className="logo-subtitle">Conferencia de Cargas</p>
          </div>
          <div className="error-message" style={{ display: 'block' }}>
            {erroEmpresa}
          </div>
          <p style={{ textAlign: 'center', color: '#64748b', fontSize: '0.9rem', marginTop: '16px' }}>
            Verifique se o QR Code esta correto ou entre em contato com a transportadora.
          </p>
        </div>
      </div>
    );
  }

  // Tela de loading
  if (modo === 'loading') {
    return (
      <div className="login-page" style={{ background: 'linear-gradient(135deg, #16a34a 0%, #15803d 50%, #14532d 100%)' }}>
        <div className="login-container" style={{ textAlign: 'center' }}>
          <span className="loading-spinner" style={{ width: '32px', height: '32px', borderColor: 'rgba(22, 163, 74, 0.2)', borderTopColor: '#16a34a' }} />
          <p style={{ color: '#64748b', marginTop: '12px' }}>Carregando...</p>
        </div>
      </div>
    );
  }

  // Verificar se o usuário está logado no dashboard principal como admin/supervisor
  const mainUser = isMainAuthenticated() ? getMainUser() : null;
  const isMainAdmin = mainUser && (mainUser.tipo === 'admin' || mainUser.tipo === 'supervisor');

  return (
    <div className="login-page" style={{ background: 'linear-gradient(135deg, #16a34a 0%, #15803d 50%, #14532d 100%)' }}>
      <PWAInstallPrompt />
      {isMainAdmin && (
        <a
          href="/"
          style={{
            position: 'fixed',
            top: '16px',
            right: '16px',
            zIndex: 50,
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 16px',
            background: 'rgba(255, 255, 255, 0.95)',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            color: '#15803d',
            fontWeight: 600,
            fontSize: '0.85rem',
            textDecoration: 'none',
            transition: 'all 0.2s',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.2)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255, 255, 255, 0.95)'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.15)'; }}
          title="Voltar ao Painel Administrativo"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"/><circle cx="12" cy="12" r="3"/></svg>
          Painel Admin
        </a>
      )}
      <div className="login-container">
        <div className="logo-section">
          {empresa?.logomarca_url ? (
            <img className="logo" src={empresa.logomarca_url} alt={empresa.nome}
              style={{ maxHeight: '60px' }}
              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
          ) : (
            <img className="logo" src="/images/logo-confereai.png" alt="Logo ConfereAi"
              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
          )}
          {empresa && (
            <p className="logo-subtitle" style={{ fontWeight: 600, fontSize: '1rem' }}>
              {empresa.nome}
            </p>
          )}
          <p className="logo-subtitle" style={{ fontSize: '0.85rem', marginTop: '4px' }}>
            {modo === 'cadastro' ? 'Cadastro de Conferente' : modo === 'alterar_pin' ? 'Alterar PIN' : 'Conferencia de Cargas'}
          </p>
        </div>

        {!isOnline && (
          <div style={{ background: 'rgba(234, 179, 8, 0.1)', border: '1px solid rgba(234, 179, 8, 0.3)', borderRadius: '10px', padding: '12px 16px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <WifiOff style={{ width: '16px', height: '16px', color: '#ca8a04', flexShrink: 0 }} />
            <p style={{ fontSize: '0.85rem', color: '#a16207', margin: 0 }}>Sem conexao. Necessario internet.</p>
          </div>
        )}

        {error && (
          <div className="error-message" style={{ display: 'block' }}>
            {error}
          </div>
        )}

        {/* FORMULÁRIO DE LOGIN */}
        {modo === 'login' && (
          <>
            {/* Lista de contas salvas */}
            {savedDiaristas.length > 0 && showSavedAccounts && !selectedAccount && (
              <div style={{ marginBottom: '20px' }}>
                <p style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '10px', fontWeight: 500 }}>
                  Conferentes neste dispositivo:
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {savedDiaristas.map((account) => (
                    <div
                      key={account.cpf}
                      onClick={() => handleSelectAccount(account)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '12px 14px',
                        background: 'rgba(22, 163, 74, 0.06)',
                        border: '1.5px solid rgba(22, 163, 74, 0.2)',
                        borderRadius: '12px',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'rgba(22, 163, 74, 0.12)';
                        e.currentTarget.style.borderColor = 'rgba(22, 163, 74, 0.4)';
                        e.currentTarget.style.transform = 'translateY(-1px)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'rgba(22, 163, 74, 0.06)';
                        e.currentTarget.style.borderColor = 'rgba(22, 163, 74, 0.2)';
                        e.currentTarget.style.transform = 'translateY(0)';
                      }}
                    >
                      {/* Avatar com iniciais */}
                      <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #16a34a, #15803d)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#fff',
                        fontWeight: 700,
                        fontSize: '0.85rem',
                        flexShrink: 0,
                      }}>
                        {getInitials(account.nome)}
                      </div>
                      {/* Info */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: '0.95rem', color: '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {account.nome}
                        </div>
                        <div style={{ fontSize: '0.8rem', color: '#94a3b8' }}>
                          CPF: {maskCpf(account.cpf)}
                        </div>
                      </div>
                      {/* Botao remover */}
                      <button
                        type="button"
                        onClick={(e) => handleRemoveAccount(e, account.cpf)}
                        title="Remover conta salva"
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: '4px',
                          fontSize: '1.1rem',
                          color: '#94a3b8',
                          lineHeight: 1,
                          flexShrink: 0,
                          borderRadius: '6px',
                          transition: 'all 0.2s',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.color = '#ef4444';
                          e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.color = '#94a3b8';
                          e.currentTarget.style.background = 'none';
                        }}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => setShowSavedAccounts(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#64748b',
                    fontSize: '0.8rem',
                    marginTop: '8px',
                    padding: '4px 0',
                    textDecoration: 'underline',
                  }}
                >
                  Usar outro CPF
                </button>
              </div>
            )}

            {/* Formulário de PIN para conta selecionada */}
            {selectedAccount && (
              <form onSubmit={handleSavedAccountLogin} autoComplete="off">
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '12px 14px',
                  background: 'rgba(22, 163, 74, 0.1)',
                  border: '1.5px solid rgba(22, 163, 74, 0.4)',
                  borderRadius: '12px',
                  marginBottom: '16px',
                }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #16a34a, #15803d)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                    fontWeight: 700,
                    fontSize: '0.85rem',
                    flexShrink: 0,
                  }}>
                    {getInitials(selectedAccount.nome)}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: '0.95rem', color: '#1e293b' }}>
                      {selectedAccount.nome}
                    </div>
                    <div style={{ fontSize: '0.8rem', color: '#94a3b8' }}>
                      CPF: {maskCpf(selectedAccount.cpf)}
                    </div>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="pin">PIN</label>
                  <div className="input-wrapper">
                    <input
                      type="password"
                      id="pin"
                      placeholder="Digite seu PIN"
                      value={pin}
                      onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
                      required
                      minLength={4}
                      autoComplete="off"
                      inputMode="numeric"
                      autoFocus
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  className="login-btn"
                  disabled={loading || pin.length < 4 || !isOnline}
                >
                  {loading ? (
                    <>
                      <span className="loading-spinner" />
                      <span>{syncStatus || 'Entrando...'}</span>
                    </>
                  ) : (
                    <span>Entrar</span>
                  )}
                </button>

                <div style={{ textAlign: 'center', marginTop: '16px' }}>
                  <button
                    type="button"
                    onClick={handleBackToAccounts}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#16a34a', fontSize: '0.9rem', fontWeight: 500, textDecoration: 'underline' }}
                  >
                    Voltar
                  </button>
                </div>
              </form>
            )}

            {/* Formulário completo de CPF + PIN */}
            {(!savedDiaristas.length || !showSavedAccounts) && !selectedAccount && (
              <form onSubmit={handleLogin} autoComplete="off">
                <div className="form-group">
                  <label htmlFor="cpf">CPF</label>
                  <div className="input-wrapper">
                    <input
                      type="tel"
                      id="cpf"
                      placeholder="000.000.000-00"
                      value={cpf}
                      onChange={(e) => setCpf(formatCpf(e.target.value))}
                      required
                      maxLength={14}
                      autoComplete="off"
                      inputMode="numeric"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="pin">PIN</label>
                  <div className="input-wrapper">
                    <input
                      type="password"
                      id="pin"
                      placeholder="Digite seu PIN"
                      value={pin}
                      onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
                      required
                      minLength={4}
                      autoComplete="off"
                      inputMode="numeric"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  className="login-btn"
                  disabled={loading || cpf.replace(/\D/g, '').length !== 11 || pin.length < 4 || !isOnline}
                >
                  {loading ? (
                    <>
                      <span className="loading-spinner" />
                      <span>{syncStatus || 'Entrando...'}</span>
                    </>
                  ) : (
                    <span>Entrar</span>
                  )}
                </button>

                <div style={{ textAlign: 'center', marginTop: '16px' }}>
                  <button
                    type="button"
                    onClick={() => { setModo('cadastro'); setError(''); setPin(''); }}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#16a34a', fontSize: '0.9rem', fontWeight: 500, textDecoration: 'underline' }}
                  >
                    Primeiro acesso? Cadastre-se
                  </button>
                </div>

                {savedDiaristas.length > 0 && !showSavedAccounts && (
                  <div style={{ textAlign: 'center', marginTop: '8px' }}>
                    <button
                      type="button"
                      onClick={() => { setShowSavedAccounts(true); setCpf(''); setPin(''); setError(''); }}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#16a34a', fontSize: '0.85rem', fontWeight: 500, textDecoration: 'underline' }}
                    >
                      Voltar para contas salvas
                    </button>
                  </div>
                )}
              </form>
            )}

            {/* Link para login por email - visível em todos os estados do modo login */}
            {!selectedAccount && (
              <div style={{ textAlign: 'center', marginTop: '12px', borderTop: '1px solid #e2e8f0', paddingTop: '12px' }}>
                <Link
                  to="/conferente/login"
                  style={{ color: '#64748b', fontSize: '0.8rem', textDecoration: 'underline' }}
                >
                  Entrar com e-mail e senha
                </Link>
              </div>
            )}
          </>
        )}

        {/* FORMULÁRIO DE CADASTRO */}
        {modo === 'cadastro' && (
          <form onSubmit={handleCadastro} autoComplete="off">
            <div className="form-group">
              <label htmlFor="cpf">CPF</label>
              <div className="input-wrapper">
                <input
                  type="tel"
                  id="cpf"
                  placeholder="000.000.000-00"
                  value={cpf}
                  onChange={(e) => setCpf(formatCpf(e.target.value))}
                  required
                  maxLength={14}
                  autoComplete="off"
                  inputMode="numeric"
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="nome">Nome Completo</label>
              <div className="input-wrapper">
                <input
                  type="text"
                  id="nome"
                  placeholder="Digite seu nome completo"
                  value={nome}
                  onChange={(e) => setNome(e.target.value)}
                  required
                  autoComplete="off"
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="pin">Crie um PIN (minimo 4 digitos)</label>
              <div className="input-wrapper">
                <input
                  type="password"
                  id="pin"
                  placeholder="Crie seu PIN"
                  value={pin}
                  onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
                  required
                  minLength={4}
                  autoComplete="off"
                  inputMode="numeric"
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="pinConfirm">Confirme o PIN</label>
              <div className="input-wrapper">
                <input
                  type="password"
                  id="pinConfirm"
                  placeholder="Confirme seu PIN"
                  value={pinConfirm}
                  onChange={(e) => setPinConfirm(e.target.value.replace(/\D/g, ''))}
                  required
                  minLength={4}
                  autoComplete="off"
                  inputMode="numeric"
                />
              </div>
            </div>

            <button
              type="submit"
              className="login-btn"
              disabled={loading || cpf.replace(/\D/g, '').length !== 11 || !nome.trim() || pin.length < 4 || pin !== pinConfirm || !isOnline}
            >
              {loading ? (
                <>
                  <span className="loading-spinner" />
                  <span>{syncStatus || 'Cadastrando...'}</span>
                </>
              ) : (
                <span>Cadastrar</span>
              )}
            </button>

            <div style={{ textAlign: 'center', marginTop: '16px' }}>
              <button
                type="button"
                onClick={() => { setModo('login'); setError(''); setPin(''); setPinConfirm(''); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#16a34a', fontSize: '0.9rem', fontWeight: 500, textDecoration: 'underline' }}
              >
                Ja tenho cadastro. Fazer login
              </button>
            </div>
          </form>
        )}

        {/* FORMULÁRIO DE ALTERAR PIN (após reset) */}
        {modo === 'alterar_pin' && (
          <form onSubmit={handleAlterarPin} autoComplete="off">
            <div style={{ background: 'rgba(234, 179, 8, 0.1)', border: '1px solid rgba(234, 179, 8, 0.3)', borderRadius: '10px', padding: '12px 16px', marginBottom: '20px' }}>
              <p style={{ fontSize: '0.85rem', color: '#a16207', margin: 0 }}>
                Seu PIN foi resetado. Crie um novo PIN para continuar.
              </p>
            </div>

            <div className="form-group">
              <label htmlFor="pinNovo">Novo PIN (minimo 4 digitos)</label>
              <div className="input-wrapper">
                <input
                  type="password"
                  id="pinNovo"
                  placeholder="Crie seu novo PIN"
                  value={pinNovo}
                  onChange={(e) => setPinNovo(e.target.value.replace(/\D/g, ''))}
                  required
                  minLength={4}
                  autoComplete="off"
                  inputMode="numeric"
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="pinNovoConfirm">Confirme o novo PIN</label>
              <div className="input-wrapper">
                <input
                  type="password"
                  id="pinNovoConfirm"
                  placeholder="Confirme seu novo PIN"
                  value={pinNovoConfirm}
                  onChange={(e) => setPinNovoConfirm(e.target.value.replace(/\D/g, ''))}
                  required
                  minLength={4}
                  autoComplete="off"
                  inputMode="numeric"
                />
              </div>
            </div>

            <button
              type="submit"
              className="login-btn"
              disabled={loading || pinNovo.length < 4 || pinNovo !== pinNovoConfirm || !isOnline}
            >
              {loading ? (
                <>
                  <span className="loading-spinner" />
                  <span>Salvando...</span>
                </>
              ) : (
                <span>Salvar novo PIN</span>
              )}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default ConferenteCadastroLogin;
